/**
 * React Query hooks for Map API endpoints (WP 5.2)
 */
import { useQuery, useQueries, type QueryClient } from "@tanstack/react-query";
import type { Map as MapboxMap } from "mapbox-gl";
import { useMemo, useEffect, useRef } from "react";
import { fetchRoads, fetchAddresses, fetchAddressesStatic, fetchAddressDeliveries, fetchAssignments, fetchAddressAggregations, fetchPostalCodes, fetchElectoralDistricts, fetchMunicipalities, fetchCounties } from "./api";
import { getBoundingBoxFromMap } from "./utils";
import { splitBoundingBoxIntoTiles, shouldUseTileLoading, calculateTileCount } from "./tileLoader";
import { useTelemetry } from "../../shared/useTelemetry";
import type {
    RoadsQueryParams,
    AddressesQueryParams,
    AssignmentsQueryParams,
    MapAddressAggregationsQueryParams,
    PostalCodesQueryParams,
    ElectoralDistrictsQueryParams,
    MunicipalitiesQueryParams,
    RoadsResponse,
    AddressesResponse,
    AddressesStaticResponse,
    AddressesDeliveriesResponse,
    AssignmentsResponse,
    AddressGridAggregationsResponse,
    PostalCodesResponse,
    ElectoralDistrictsResponse,
    MunicipalitiesResponse,
    AddressFlyerSummary,
    AddressFlyerStatus,
    AddressDeliveryFeatureProperties,
    AddressDeliveryUpdate,
    GeoJsonFeature,
    RoadFeatureProperties,
    AddressFeatureProperties
} from "./types";

/**
 * Hook options for map queries
 */
export interface MapQueryOptions {
    enabled?: boolean;
    refetchInterval?: number | false;
    staleTime?: number;
}

/** Incremented by requestMapDeliveriesFullFetch(); next deliveries queryFn consumes all pending. */
let mapDeliveriesForceFullPendingCount = 0;

/**
 * Call before invalidating map address queries after any delivery write (mark delivered, undo, bulk, etc.).
 * Ensures the next GET addresses/deliveries omits updatedSince so the client gets a full bbox snapshot
 * (delta responses are sparse and must not be merged as if complete).
 */
export function requestMapDeliveriesFullFetch(): void {
    mapDeliveriesForceFullPendingCount += 1;
}

function normalizeMapId(id: string): string {
    return id.trim().toLowerCase();
}

function mapDeliveryUpdateMatchesSelectedFlyer(d: AddressDeliveryUpdate, selectedFlyerTypeId: string): boolean {
    const sel = normalizeMapId(selectedFlyerTypeId);
    return (
        (d.flyerTypeId != null && normalizeMapId(d.flyerTypeId) === sel) ||
        (d.campaignFlyerId != null && normalizeMapId(d.campaignFlyerId) === sel)
    );
}

/**
 * Optimistically adds a placeholder delivery for one address+flyer to all cached
 * ["map","addresses","deliveries", ...] queries so combined map data shows delivered (green) immediately.
 * The placeholder is replaced by real server data when the next full fetch completes.
 */
export function addDeliveryToMapCache(
    queryClient: QueryClient,
    addressPointId: string,
    selectedFlyerTypeId: string
): void {
    const targetId = normalizeMapId(addressPointId);
    queryClient.setQueriesData<AddressesDeliveriesResponse>(
        { queryKey: ["map", "addresses", "deliveries"] },
        (old) => {
            if (!old) {
                return old;
            }
            // Check if the address already has a matching delivery — nothing to add
            const existing = old.features.find(
                (f) => normalizeMapId(f.properties.addressPointId) === targetId
            );
            if (existing) {
                const alreadyHas = existing.properties.deliveries.some((d) =>
                    mapDeliveryUpdateMatchesSelectedFlyer(d, selectedFlyerTypeId)
                );
                if (alreadyHas) {
                    return old;
                }
                // Add placeholder delivery to existing feature
                const updated: GeoJsonFeature<AddressDeliveryFeatureProperties> = {
                    ...existing,
                    properties: {
                        ...existing.properties,
                        deliveries: [
                            ...existing.properties.deliveries,
                            {
                                addressPointId,
                                flyerTypeId: selectedFlyerTypeId,
                                campaignFlyerId: null,
                                distributorAssignmentId: null,
                                quantity: 1,
                                lastDeliveredAt: new Date().toISOString(),
                                recordedById: ""
                            }
                        ]
                    }
                };
                return {
                    ...old,
                    features: old.features.map((f) =>
                        normalizeMapId(f.properties.addressPointId) === targetId ? updated : f
                    ),
                    metadata: { ...old.metadata, generatedAt: new Date().toISOString() }
                };
            }
            // Address not in cache yet — add a new feature with placeholder delivery
            const newFeature: GeoJsonFeature<AddressDeliveryFeatureProperties> = {
                type: "Feature",
                id: addressPointId,
                geometry: { type: "Point", coordinates: [0, 0] }, // coordinates not used for status
                properties: {
                    addressPointId,
                    deliveries: [
                        {
                            addressPointId,
                            flyerTypeId: selectedFlyerTypeId,
                            campaignFlyerId: null,
                            distributorAssignmentId: null,
                            quantity: 1,
                            lastDeliveredAt: new Date().toISOString(),
                            recordedById: ""
                        }
                    ]
                }
            };
            return {
                ...old,
                features: [...old.features, newFeature],
                metadata: { ...old.metadata, generatedAt: new Date().toISOString() }
            };
        }
    );
}

/**
 * Optimistically removes delivery rows for one address and selected flyer from all cached
 * ["map","addresses","deliveries", ...] queries so combined map data shows pending (blue) immediately after undo.
 */
export function removeAddressFlyerDeliveriesFromMapCache(
    queryClient: QueryClient,
    addressPointId: string,
    selectedFlyerTypeId: string
): void {
    const targetId = normalizeMapId(addressPointId);
    queryClient.setQueriesData<AddressesDeliveriesResponse>(
        { queryKey: ["map", "addresses", "deliveries"] },
        (old) => {
            if (!old?.features?.length) {
                return old;
            }
            let changed = false;
            const nextFeatures: GeoJsonFeature<AddressDeliveryFeatureProperties>[] = [];
            for (const f of old.features) {
                if (normalizeMapId(f.properties.addressPointId) !== targetId) {
                    nextFeatures.push(f);
                    continue;
                }
                const deliveries = f.properties.deliveries.filter((d) => {
                    const drop = mapDeliveryUpdateMatchesSelectedFlyer(d, selectedFlyerTypeId);
                    if (drop) {
                        changed = true;
                    }
                    return !drop;
                });
                if (deliveries.length !== f.properties.deliveries.length) {
                    changed = true;
                }
                // Keep the feature even when deliveries is empty — computeCombinedAddresses
                // treats a present-but-empty feature as "explicitly cleared" and shows pending (blue),
                // whereas an absent feature in a delta response falls back to previous state.
                nextFeatures.push({
                    ...f,
                    properties: { ...f.properties, deliveries }
                });
            }
            if (!changed) {
                return old;
            }
            return {
                ...old,
                features: nextFeatures,
                metadata: {
                    ...old.metadata,
                    generatedAt: new Date().toISOString()
                }
            };
        }
    );
}

/**
 * Merges multiple GeoJSON feature collections into one
 */
function mergeRoadResponses(responses: RoadsResponse[]): RoadsResponse {
    if (responses.length === 0) {
        return {
            type: "FeatureCollection",
            features: [],
            metadata: {
                campaignId: "",
                featureCount: 0,
                lastUpdated: new Date().toISOString(),
                generatedAt: new Date().toISOString(),
                boundingBoxWidth: 0,
                boundingBoxHeight: 0,
                dataVersion: ""
            },
            crs: "EPSG:4326"
        };
    }

    // Merge features, removing duplicates by ID
    const featureMap = new Map<string | number, GeoJsonFeature<RoadFeatureProperties>>();
    responses.forEach((response) => {
        response.features.forEach((feature) => {
            const id = feature.id ?? feature.properties.roadSegmentId;
            if (id !== undefined) {
                featureMap.set(id, feature);
            }
        });
    });

    const mergedFeatures = Array.from(featureMap.values());
    const firstResponse = responses[0];

    return {
        type: "FeatureCollection",
        features: mergedFeatures,
        metadata: {
            ...firstResponse.metadata,
            featureCount: mergedFeatures.length,
            lastUpdated: responses.reduce(
                (latest, r) => (r.metadata.lastUpdated > latest ? r.metadata.lastUpdated : latest),
                firstResponse.metadata.lastUpdated
            )
        },
        crs: firstResponse.crs
    };
}

/**
 * React Query hook for fetching roads data
 * Uses tile-based loading when zoomed out to load data area by area
 */
export const useRoadFeatures = (
    params: Omit<RoadsQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    // Determine if we should use tile-based loading
    const useTiles = useMemo(() => {
        return bbox ? shouldUseTileLoading(bbox) : false;
    }, [bbox]);

    const tileCount = useMemo(() => {
        return bbox ? calculateTileCount(bbox) : 3;
    }, [bbox]);

    // Tile-based loading: split bbox into tiles and load each
    const tiles = useMemo(() => {
        if (!bbox || !useTiles) return [];
        return splitBoundingBoxIntoTiles(bbox, tileCount);
    }, [bbox, useTiles, tileCount]);

    // Create queries for each tile - Load all tiles in parallel
    const tileQueries = useQueries({
        queries: useTiles && tiles.length > 0
            ? tiles.map((tile, index) => {
                const isEnabled = options?.enabled !== false && !!params.campaignId;

                return {
                    queryKey: ["map", "roads", "tile", { ...params, bbox: tile.bbox }, index],
                    queryFn: async ({ signal }) => {
                        try {
                            const result = await fetchRoads({ ...params, bbox: tile.bbox }, signal);
                            return result;
                        } catch (error) {
                            telemetry.trackError("map.fetch.failure", { source: "roads", error, bbox: tile.bbox });
                            throw error;
                        }
                    },
                    enabled: isEnabled,
                    staleTime: options?.staleTime ?? 24 * 60 * 60 * 1000, // 24 hours (Phase 3.1: Very long cache for static roads)
                    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days (keep in memory)
                    refetchOnWindowFocus: false, // Don't refetch on focus (Phase 3.1)
                    refetchOnReconnect: false, // Don't refetch on reconnect (Phase 3.1)
                    retry: false,
                    placeholderData: (previousData: RoadsResponse | undefined) => previousData
                };
            })
            : []
    });

    // Single query for non-tile loading
    const singleQuery = useQuery<RoadsResponse, Error>({
        queryKey: ["map", "roads", { ...params, bbox }],
        queryFn: async ({ signal }) => {
            try {
                const result = await fetchRoads({ ...params, bbox: bbox! }, signal);
                return result;
            } catch (error) {
                // Don't log cancelled requests as errors
                if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
                    throw error; // Re-throw but don't log
                }
                telemetry.trackError("map.fetch.failure", { source: "roads", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false && !!params.campaignId && !!bbox && !useTiles,
        refetchInterval: options?.refetchInterval,
        staleTime: options?.staleTime ?? 24 * 60 * 60 * 1000, // 24 hours (Phase 3.1: Very long cache for static roads)
        refetchOnWindowFocus: false, // Don't refetch on focus (Phase 3.1)
        refetchOnReconnect: false, // Don't refetch on reconnect (Phase 3.1)
        retry: false, // Don't retry cancelled requests
        placeholderData: (previousData) => previousData,
        gcTime: 7 * 24 * 60 * 60 * 1000 // 7 days (keep in memory)
    });

    // Merge tile results - use a more stable dependency
    const tileDataSignature = useMemo(() => {
        if (!useTiles || tileQueries.length === 0) return '';
        return tileQueries
            .map((q, i) => `${i}:${q.dataUpdatedAt}:${q.data?.features.length ?? 0}`)
            .join('|');
    }, [useTiles, tileQueries]);

    const mergedData = useMemo(() => {
        if (!useTiles) {
            return singleQuery.data;
        }

        const successfulTiles = tileQueries
            .filter((q) => q.data)
            .map((q) => q.data!);

        if (successfulTiles.length === 0) {
            return undefined;
        }

        return mergeRoadResponses(successfulTiles);
    }, [useTiles, singleQuery.data, tileDataSignature]);

    // Determine loading/error state
    const isLoading = useTiles
        ? tileQueries.some((q) => q.isLoading)
        : singleQuery.isLoading;
    const isError = useTiles
        ? tileQueries.some((q) => q.isError)
        : singleQuery.isError;
    const error = useTiles
        ? tileQueries.find((q) => q.error)?.error
        : singleQuery.error;

    return {
        data: mergedData,
        isLoading,
        isError,
        error,
        isFetching: useTiles
            ? tileQueries.some((q) => q.isFetching)
            : singleQuery.isFetching
    };
};

/**
 * Merges multiple address GeoJSON feature collections into one
 */
function mergeAddressResponses(responses: AddressesResponse[]): AddressesResponse {
    if (responses.length === 0) {
        return {
            type: "FeatureCollection",
            features: [],
            metadata: {
                campaignId: "",
                featureCount: 0,
                blockedCount: 0,
                hiddenCount: 0,
                totalFlyerStatuses: 0,
                lastUpdated: new Date().toISOString(),
                generatedAt: new Date().toISOString(),
                flyerSummaries: {},
                dataVersion: ""
            },
            crs: "EPSG:4326"
        };
    }

    // Merge features, removing duplicates by ID
    const featureMap = new Map<string | number, GeoJsonFeature<AddressFeatureProperties>>();
    responses.forEach((response) => {
        response.features.forEach((feature) => {
            const id = feature.id ?? feature.properties.addressPointId;
            if (id !== undefined) {
                featureMap.set(id, feature);
            }
        });
    });

    const mergedFeatures = Array.from(featureMap.values());
    const firstResponse = responses[0];

    // Merge metadata
    const totalBlocked = responses.reduce((sum, r) => sum + r.metadata.blockedCount, 0);
    const totalHidden = responses.reduce((sum, r) => sum + r.metadata.hiddenCount, 0);
    const totalFlyerStatuses = responses.reduce((sum, r) => sum + r.metadata.totalFlyerStatuses, 0);

    // Merge flyer summaries
    const mergedSummaries: Record<string, AddressFlyerSummary> = {};
    responses.forEach((response) => {
        Object.entries(response.metadata.flyerSummaries).forEach(([flyerTypeId, summary]) => {
            if (!mergedSummaries[flyerTypeId]) {
                mergedSummaries[flyerTypeId] = { ...summary };
            } else {
                mergedSummaries[flyerTypeId].deliveredCount += summary.deliveredCount;
                if (summary.lastDeliveredAt && (!mergedSummaries[flyerTypeId].lastDeliveredAt ||
                    summary.lastDeliveredAt > mergedSummaries[flyerTypeId].lastDeliveredAt!)) {
                    mergedSummaries[flyerTypeId].lastDeliveredAt = summary.lastDeliveredAt;
                }
            }
        });
    });

    return {
        type: "FeatureCollection",
        features: mergedFeatures,
        metadata: {
            ...firstResponse.metadata,
            featureCount: mergedFeatures.length,
            blockedCount: totalBlocked,
            hiddenCount: totalHidden,
            totalFlyerStatuses,
            lastUpdated: responses.reduce(
                (latest, r) => (r.metadata.lastUpdated > latest ? r.metadata.lastUpdated : latest),
                firstResponse.metadata.lastUpdated
            ),
            flyerSummaries: mergedSummaries
        },
        crs: firstResponse.crs
    };
}

/**
 * React Query hook for fetching addresses data
 * Uses tile-based loading when zoomed out to load data area by area
 */
export const useAddressFeatures = (
    params: Omit<AddressesQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    // Determine if we should use tile-based loading
    const useTiles = useMemo(() => {
        return bbox ? shouldUseTileLoading(bbox) : false;
    }, [bbox]);

    const tileCount = useMemo(() => {
        return bbox ? calculateTileCount(bbox) : 3;
    }, [bbox]);

    // Tile-based loading: split bbox into tiles and load each
    const tiles = useMemo(() => {
        if (!bbox || !useTiles) return [];
        return splitBoundingBoxIntoTiles(bbox, tileCount);
    }, [bbox, useTiles, tileCount]);

    // Create queries for each tile - Load all tiles in parallel
    const tileQueries = useQueries({
        queries: useTiles && tiles.length > 0
            ? tiles.map((tile, index) => {
                const isEnabled = options?.enabled !== false && !!params.campaignId;

                return {
                    queryKey: ["map", "addresses", "tile", { ...params, bbox: tile.bbox }, index],
                    queryFn: async ({ signal }) => {
                        try {
                            const result = await fetchAddresses({ ...params, bbox: tile.bbox }, signal);
                            return result;
                        } catch (error) {
                            // Don't log cancelled requests as errors
                            if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
                                throw error; // Re-throw but don't log
                            }
                            telemetry.trackError("map.fetch.failure", { source: "addresses", error, bbox: tile.bbox, tileIndex: index });
                            throw error;
                        }
                    },
                    enabled: isEnabled,
                    staleTime: options?.staleTime ?? 30 * 1000, // 30 seconds - reduce refetching
                    gcTime: 5 * 60 * 1000,
                    retry: false,
                    placeholderData: (previousData: AddressesResponse | undefined) => previousData // Keep cached data on error
                };
            })
            : []
    });

    // Single query for non-tile loading
    const singleQuery = useQuery<AddressesResponse, Error>({
        queryKey: ["map", "addresses", { ...params, bbox }],
        queryFn: async ({ signal }) => {
            try {
                const result = await fetchAddresses({ ...params, bbox: bbox! }, signal);
                return result;
            } catch (error) {
                // Don't log cancelled requests as errors
                if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
                    throw error; // Re-throw but don't log
                }
                telemetry.trackError("map.fetch.failure", { source: "addresses", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false && !!params.campaignId && !!bbox && !useTiles,
        refetchInterval: options?.refetchInterval,
        staleTime: options?.staleTime ?? 30 * 1000, // 30 seconds - reduce refetching
        retry: false, // Don't retry cancelled requests
        placeholderData: (previousData) => previousData, // Keep cached data on error
        gcTime: 5 * 60 * 1000
    });

    // Merge tile results - use a more stable dependency
    const addressTileDataSignature = useMemo(() => {
        if (!useTiles || tileQueries.length === 0) return '';
        return tileQueries
            .map((q, i) => `${i}:${q.dataUpdatedAt}:${q.data?.features.length ?? 0}`)
            .join('|');
    }, [useTiles, tileQueries]);

    const mergedData = useMemo(() => {
        if (!useTiles) {
            return singleQuery.data;
        }

        const successfulTiles = tileQueries
            .filter((q) => q.data)
            .map((q) => q.data!);

        if (successfulTiles.length === 0) {
            return undefined;
        }

        return mergeAddressResponses(successfulTiles);
    }, [useTiles, singleQuery.data, addressTileDataSignature]);

    // Determine loading/error state
    const isLoading = useTiles
        ? tileQueries.some((q) => q.isLoading)
        : singleQuery.isLoading;
    const isError = useTiles
        ? tileQueries.some((q) => q.isError)
        : singleQuery.isError;
    const error = useTiles
        ? tileQueries.find((q) => q.error)?.error
        : singleQuery.error;

    // Calculate isFetching separately to avoid including tileQueries array in dependencies
    const isFetching = useTiles
        ? tileQueries.some((q) => q.isFetching)
        : singleQuery.isFetching;

    // Use useMemo to create a stable reference that changes when mergedData changes
    // This ensures React Query and consumers detect the change
    const returnValue = useMemo(() => {
        return {
            data: mergedData,
            isLoading,
            isError,
            error,
            isFetching
        };
    }, [mergedData, isLoading, isError, error, isFetching]);

    return returnValue;
};

/**
 * React Query hook for fetching assignments data
 */
export const useAssignmentPolygons = (
    params: Omit<AssignmentsQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    const queryParams: AssignmentsQueryParams = useMemo(
        () => ({
            ...params,
            bbox
        }),
        [params, bbox]
    );

    return useQuery<AssignmentsResponse, Error>({
        queryKey: ["map", "assignments", queryParams],
        queryFn: async () => {
            try {
                const result = await fetchAssignments(queryParams);
                return result;
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "assignments", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false && (!!params.campaignId || !!params.municipalityId) && !!bbox,
        refetchInterval: options?.refetchInterval,
        staleTime: options?.staleTime ?? 60000, // 60 seconds default (assignments change less frequently)
        placeholderData: (previousData) => previousData, // Keep previous data on error
        gcTime: 5 * 60 * 1000 // Keep cache for 5 minutes
    });
};

/**
 * Hook to listen to map viewport changes and update query bbox
 * This should be used in the MapView component to automatically refresh data on pan/zoom
 */
export const useMapViewportListener = (
    map: MapboxMap | null,
    onViewportChange: (bbox: string) => void,
    debounceMs: number = 1500 // Increased to 1.5s to reduce query frequency
) => {
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    const onViewportChangeRef = useRef(onViewportChange);
    const mapRef = useRef(map);
    const lastBboxRef = useRef<string | null>(null);

    useEffect(() => {
        (window as any).__resetViewportLastBbox = () => {
            lastBboxRef.current = null;
        };

        (window as any).__triggerViewportUpdate = () => {
            const currentMap = mapRef.current;
            if (!currentMap) {
                return false;
            }

            const bbox = getBoundingBoxFromMap(currentMap);
            if (!bbox) {
                return false;
            }

            // Reset lastBboxRef to ensure this update is not filtered out
            lastBboxRef.current = null;

            // Update lastBboxRef with new bbox to prevent duplicate triggers
            lastBboxRef.current = bbox;

            onViewportChangeRef.current(bbox);
            return true;
        };

        return () => {
            delete (window as any).__resetViewportLastBbox;
            delete (window as any).__triggerViewportUpdate;
        };
    }, []);

    // Keep refs in sync
    useEffect(() => {
        onViewportChangeRef.current = onViewportChange;
        mapRef.current = map;
    }, [onViewportChange, map]);

    useEffect(() => {
        const currentMap = mapRef.current;

        if (!currentMap) {
            return;
        }

        const handleViewportChange = () => {
            if (!currentMap.isStyleLoaded()) {
                return;
            }

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            debounceTimeoutRef.current = setTimeout(() => {
                const map = mapRef.current;
                if (!map) {
                    return;
                }
                const bbox = getBoundingBoxFromMap(map);

                if (bbox) {
                    const lastBbox = lastBboxRef.current;
                    if (lastBbox && bbox === lastBbox) {
                        return;
                    }

                    if (lastBbox) {
                        const [lastMinLon, lastMinLat, lastMaxLon, lastMaxLat] = lastBbox.split(',').map(Number);
                        const [minLon, minLat, maxLon, maxLat] = bbox.split(',').map(Number);

                        const lastWidth = lastMaxLon - lastMinLon;
                        const lastHeight = lastMaxLat - lastMinLat;
                        const newWidth = maxLon - minLon;
                        const newHeight = maxLat - minLat;

                        const overlapLon = Math.max(0, Math.min(lastMaxLon, maxLon) - Math.max(lastMinLon, minLon));
                        const overlapLat = Math.max(0, Math.min(lastMaxLat, maxLat) - Math.max(lastMinLat, minLat));
                        const overlapArea = overlapLon * overlapLat;
                        const lastArea = lastWidth * lastHeight;
                        const newArea = newWidth * newHeight;
                        const maxArea = Math.max(lastArea, newArea);
                        const overlapPercent = maxArea > 0 ? (overlapArea / maxArea) * 100 : 0;

                        if (overlapPercent >= 85) {
                            return;
                        }
                    }

                    lastBboxRef.current = bbox;
                    onViewportChangeRef.current(bbox);
                }
            }, debounceMs);
        };

        if (currentMap.isStyleLoaded()) {
            const initialBbox = getBoundingBoxFromMap(currentMap);
            if (initialBbox) {
                lastBboxRef.current = initialBbox;
                onViewportChangeRef.current(initialBbox);
            }

            currentMap.on("move", handleViewportChange);
            currentMap.on("moveend", handleViewportChange);
            currentMap.on("zoomend", handleViewportChange);
        } else {
            const handleStyleLoad = () => {
                const map = mapRef.current;
                if (map) {
                    const initialBbox = getBoundingBoxFromMap(map);
                    if (initialBbox) {
                        lastBboxRef.current = initialBbox;
                        onViewportChangeRef.current(initialBbox);
                    }

                    map.on("move", handleViewportChange);
                    map.on("moveend", handleViewportChange);
                    map.on("zoomend", handleViewportChange);
                }
            };
            currentMap.once("style.load", handleStyleLoad);

            return () => {
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                }
                const map = mapRef.current;
                if (map) {
                    map.off("style.load", handleStyleLoad);
                    map.off("move", handleViewportChange);
                    map.off("moveend", handleViewportChange);
                    map.off("zoomend", handleViewportChange);
                }
            };
        }

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            const map = mapRef.current;
            if (map) {
                map.off("move", handleViewportChange);
                map.off("moveend", handleViewportChange);
                map.off("zoomend", handleViewportChange);
            }
        };
    }, [map, debounceMs]); // Removed onViewportChange from dependencies since we use ref
};

/**
 * Phase 1: Hook for fetching static address locations (long cache, invalidates on import)
 * Static data: location, street, city, etc. (never changes except on import)
 */
export const useAddressLocations = (
    params: Omit<AddressesQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    return useQuery<AddressesStaticResponse, Error>({
        queryKey: ["map", "addresses", "static", { ...params, bbox }],
        queryFn: async ({ signal }) => {
            try {
                const result = await fetchAddressesStatic({ ...params, bbox: bbox! }, signal);
                return result;
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "addresses.static", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false && !!params.campaignId && !!bbox,
        staleTime: options?.staleTime ?? 0,
        gcTime: 24 * 60 * 60 * 1000, // 24 hours (keep in memory)
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        placeholderData: (previousData: AddressesStaticResponse | undefined) => previousData
    });
};

/**
 * Phase 1: Hook for fetching address delivery updates (short cache, auto-refresh)
 * Dynamic data: only delivery statuses (changes frequently)
 */
export const useAddressDeliveries = (
    params: Omit<AddressesQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();
    const lastUpdatedRef = useRef<Date | null>(null);
    const lastBboxRef = useRef<string | undefined>(undefined);
    const lastFlyerTypeIdRef = useRef<string | null | undefined>(undefined);
    const lastAssignmentIdRef = useRef<string | undefined>(undefined);

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    const query = useQuery<AddressesDeliveriesResponse, Error>({
        queryKey: ["map", "addresses", "deliveries", { ...params, bbox }],
        queryFn: async ({ signal }) => {
            try {
                if (mapDeliveriesForceFullPendingCount > 0) {
                    mapDeliveriesForceFullPendingCount = 0;
                    lastUpdatedRef.current = null;
                }

                // Check if any filter has changed - if so, fetch ALL deliveries (not delta)
                const bboxChanged = bbox !== lastBboxRef.current;
                const flyerTypeIdChanged = params.flyerTypeId !== lastFlyerTypeIdRef.current;
                const assignmentIdChanged = params.assignmentId !== lastAssignmentIdRef.current;

                const anyFilterChanged = bboxChanged || flyerTypeIdChanged || assignmentIdChanged;

                // Only use updatedSince if NOTHING changed (for auto-refresh on same viewport/filters)
                const updatedSince = anyFilterChanged ? undefined : lastUpdatedRef.current?.toISOString();
                // Track whether this is a delta so the merge can handle it correctly
                const isDelta = updatedSince !== undefined;

                const result = await fetchAddressDeliveries(
                    { ...params, bbox: bbox! },
                    updatedSince,
                    signal
                );

                // Update refs for next request
                lastBboxRef.current = bbox;
                lastFlyerTypeIdRef.current = params.flyerTypeId;
                lastAssignmentIdRef.current = params.assignmentId;

                // Update timestamp for next delta request (only if nothing changed)
                if (!anyFilterChanged) {
                    lastUpdatedRef.current = new Date();
                }

                return { ...result, metadata: { ...result.metadata, isDelta } };
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "addresses.deliveries", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false && !!params.campaignId && !!bbox,
        staleTime: options?.staleTime ?? 60 * 1000, // 60 seconds - data is valid for a minute before refetch
        refetchInterval: options?.refetchInterval ?? 2 * 60 * 1000, // Auto-refresh every 2 minutes to see colleague deliveries
        retry: false,
        gcTime: 30 * 60 * 1000 // 30 minutes - keep in cache longer to preserve delivery statuses
    });

    return query;
};

/**
 * Phase 2: Hook for fetching address grid aggregations (zoom-based aggregated data)
 */
export const useAddressAggregations = (
    params: Omit<MapAddressAggregationsQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    // Use zoomLevel from params if provided, otherwise get from map
    const zoomLevel = useMemo(() => {
        return params.zoomLevel ?? map?.getZoom() ?? undefined;
    }, [params.zoomLevel, map]);

    return useQuery<AddressGridAggregationsResponse, Error>({
        queryKey: ["map", "addresses", "aggregations", { ...params, bbox, zoomLevel }],
        queryFn: async ({ signal }) => {
            try {
                const result = await fetchAddressAggregations(
                    { ...params, bbox: bbox!, zoomLevel },
                    signal
                );
                return result;
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "addresses.aggregations", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false && !!params.campaignId && !!bbox,
        staleTime: options?.staleTime ?? 30 * 1000, // 30 seconds - reduce refetching
        retry: false,
        gcTime: 5 * 60 * 1000
    });
};

/**
 * Phase 3.2: Zoom-aware hook that switches between aggregations (zoom < 15) and individual points (zoom >= 15)
 * Uses aggregations when zoomed out to reduce data load, individual points when zoomed in
 */
export const useZoomAwareAddresses = (
    params: Omit<AddressesQueryParams, "bbox"> & { bbox?: string },
    map?: MapboxMap | null,
    options?: MapQueryOptions & { currentZoom?: number }
) => {
    // Use explicit zoom from options if provided, otherwise get from map
    // Aggregations threshold: 15 (aggregations show until zoom 15, individual points from zoom 15+)
    const zoom = options?.currentZoom ?? map?.getZoom() ?? 15;
    const useAggregations = zoom < 15;

    // Keep reference to previous combined result to preserve flyerStatuses when deliveries data is temporarily unavailable
    const previousCombinedRef = useRef<AddressesResponse | null>(null);

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    // Aggregations query (for zoom < 15)
    // Backend will auto-determine precisions based on zoomLevel
    const aggregationsQuery = useAddressAggregations(
        {
            campaignId: params.campaignId,
            bbox,
            precisions: undefined, // Let backend determine based on zoomLevel
            zoomLevel: zoom
        },
        map,
        {
            ...options,
            enabled: options?.enabled !== false && useAggregations && !!params.campaignId && !!bbox
        }
    );

    // Individual addresses query (for zoom >= 15)
    // Combine static + deliveries for individual points
    const staticEnabled = options?.enabled !== false && !useAggregations && !!params.campaignId && !!bbox && params.campaignId !== "";

    const staticQuery = useAddressLocations(
        { ...params, bbox },
        map,
        {
            ...options,
            enabled: staticEnabled
        }
    );

    const deliveriesQuery = useAddressDeliveries(
        { ...params, bbox },
        map,
        {
            ...options,
            enabled: staticEnabled
        }
    );


    // Merge static and deliveries into combined address response
    // IMPORTANT: Allow static data even if deliveries is not loaded yet, so addresses appear immediately
    // Deliveries will be merged in when available, and existing flyerStatuses will be preserved
    // useMemo: stable reference when static/deliveries unchanged — avoids MapView re-applying the layer
    // every render (which overwrote optimistic isHidden/block/position with stale static data).
    const combinedAddresses = useMemo((): AddressesResponse | undefined => {
        if (useAggregations || !staticQuery.data) {
            return undefined;
        }

        // If deliveries data is not available yet, try to preserve flyerStatuses from previous result
        // This prevents green circles from turning blue when deliveries cache expires or is temporarily unavailable
        if (!deliveriesQuery.data) {
            // Create a map of previous flyerStatuses by addressPointId if we have previous data
            const previousFlyerStatusesMap = new Map<string, AddressFlyerStatus[]>();
            if (previousCombinedRef.current && previousCombinedRef.current.features) {
                previousCombinedRef.current.features.forEach((feature) => {
                    const addressId = feature.properties.addressPointId;
                    if (feature.properties.flyerStatuses && feature.properties.flyerStatuses.length > 0) {
                        previousFlyerStatusesMap.set(addressId, feature.properties.flyerStatuses);
                    }
                });
            }

            // Map static features, preserving flyerStatuses from previous result if available
            const featuresWithoutDeliveries = staticQuery.data.features.map((staticFeature) => {
                const addressId = staticFeature.properties.addressPointId;
                const preservedFlyerStatuses = previousFlyerStatusesMap.get(addressId) || [];

                return {
                    ...staticFeature,
                    properties: {
                        ...staticFeature.properties,
                        flyerStatuses: preservedFlyerStatuses // Preserve from previous result, or empty array
                    } as AddressFeatureProperties
                };
            });

            // Calculate flyerSummaries from preserved flyerStatuses
            const preservedFlyerSummaries: Record<string, AddressFlyerSummary> = {};
            previousFlyerStatusesMap.forEach((flyerStatuses) => {
                flyerStatuses.forEach((status) => {
                    // Use flyerTypeId if available, otherwise use campaignFlyerId
                    const key = status.flyerTypeId ?? status.campaignFlyerId;
                    if (!key) {
                        return; // Skip if both are null/undefined
                    }
                    if (!preservedFlyerSummaries[key]) {
                        preservedFlyerSummaries[key] = {
                            flyerTypeId: key, // Use the key (either flyerTypeId or campaignFlyerId)
                            deliveredCount: 0,
                            lastDeliveredAt: null
                        };
                    }
                    if (status.deliveredQuantity > 0) {
                        preservedFlyerSummaries[key].deliveredCount += status.deliveredQuantity;
                        if (status.lastDeliveredAt && (!preservedFlyerSummaries[key].lastDeliveredAt ||
                            status.lastDeliveredAt > preservedFlyerSummaries[key].lastDeliveredAt!)) {
                            preservedFlyerSummaries[key].lastDeliveredAt = status.lastDeliveredAt;
                        }
                    }
                });
            });

            const result = {
                type: "FeatureCollection" as const,
                features: featuresWithoutDeliveries,
                metadata: {
                    campaignId: staticQuery.data.metadata.campaignId,
                    featureCount: featuresWithoutDeliveries.length,
                    blockedCount: staticQuery.data.metadata.blockedCount,
                    hiddenCount: staticQuery.data.metadata.hiddenCount,
                    totalFlyerStatuses: Array.from(previousFlyerStatusesMap.values()).reduce((sum, statuses) => sum + statuses.length, 0),
                    lastUpdated: previousCombinedRef.current?.metadata.lastUpdated || staticQuery.data.metadata.lastUpdated,
                    generatedAt: new Date().toISOString(),
                    flyerSummaries: preservedFlyerSummaries,
                    dataVersion: staticQuery.data.metadata.dataVersion
                },
                crs: staticQuery.data.crs
            } as AddressesResponse;

            // Update previous ref for next time
            previousCombinedRef.current = result;

            return result;
        }

        // Merge static addresses with delivery data
        const isDelta = deliveriesQuery.data.metadata.isDelta ?? false;

        const deliveryMap = new Map<string, AddressesDeliveriesResponse["features"][0]>();
        deliveriesQuery.data.features.forEach((feature) => {
            deliveryMap.set(feature.properties.addressPointId, feature);
        });

        // For delta responses, build a lookup of the previous flyerStatuses so addresses
        // absent from the (sparse) delta can preserve their existing status rather than
        // incorrectly resetting to pending (blue).
        const prevFlyerStatusesMap = new Map<string, AddressFlyerStatus[]>();
        if (isDelta && previousCombinedRef.current) {
            for (const f of previousCombinedRef.current.features) {
                if (f.properties.flyerStatuses && f.properties.flyerStatuses.length > 0) {
                    prevFlyerStatusesMap.set(f.properties.addressPointId, f.properties.flyerStatuses);
                }
            }
        }

        const mergedFeatures = staticQuery.data.features.map((staticFeature) => {
            const addressId = staticFeature.properties.addressPointId;
            const deliveryFeature = deliveryMap.get(addressId);

            let flyerStatuses: AddressFlyerStatus[];
            if (deliveryFeature) {
                // Address is explicitly present in the response (may have deliveries: [] after undo)
                flyerStatuses = deliveryFeature.properties.deliveries.map((delivery) => ({
                    addressPointId: delivery.addressPointId,
                    campaignId: params.campaignId,
                    flyerTypeId: delivery.flyerTypeId ?? null,
                    campaignFlyerId: delivery.campaignFlyerId ?? null,
                    distributorAssignmentId: delivery.distributorAssignmentId ?? null,
                    deliveredQuantity: delivery.quantity,
                    lastDeliveredAt: delivery.lastDeliveredAt,
                    recordedById: delivery.recordedById
                }));
            } else if (isDelta) {
                // Delta response: address absent means it hasn't changed — preserve previous status
                flyerStatuses = prevFlyerStatusesMap.get(addressId) ?? [];
            } else {
                // Full response: address absent means it genuinely has no deliveries
                flyerStatuses = [];
            }

            return {
                ...staticFeature,
                properties: {
                    ...staticFeature.properties,
                    flyerStatuses
                } as AddressFeatureProperties
            };
        });

        // Calculate flyerSummaries from deliveries
        // Use flyerTypeId if available, otherwise use campaignFlyerId (for campaign-specific flyers)
        const flyerSummaries: Record<string, AddressFlyerSummary> = {};
        deliveriesQuery.data.features.forEach((feature) => {
            feature.properties.deliveries.forEach((delivery) => {
                const key = delivery.flyerTypeId ?? delivery.campaignFlyerId;
                if (!key) {
                    return; // Skip if both are null/undefined
                }
                if (!flyerSummaries[key]) {
                    flyerSummaries[key] = {
                        flyerTypeId: key,
                        deliveredCount: 0,
                        lastDeliveredAt: null
                    };
                }
                flyerSummaries[key].deliveredCount += delivery.quantity;
                if (delivery.lastDeliveredAt && (!flyerSummaries[key].lastDeliveredAt ||
                    delivery.lastDeliveredAt > flyerSummaries[key].lastDeliveredAt!)) {
                    flyerSummaries[key].lastDeliveredAt = delivery.lastDeliveredAt;
                }
            });
        });

        const result = {
            type: "FeatureCollection" as const,
            features: mergedFeatures,
            metadata: {
                campaignId: staticQuery.data.metadata.campaignId,
                featureCount: mergedFeatures.length,
                blockedCount: staticQuery.data.metadata.blockedCount,
                hiddenCount: staticQuery.data.metadata.hiddenCount,
                totalFlyerStatuses: deliveriesQuery.data.metadata.deliveryUpdateCount,
                lastUpdated: deliveriesQuery.data.metadata.lastUpdated,
                generatedAt: new Date().toISOString(),
                flyerSummaries,
                dataVersion: staticQuery.data.metadata.dataVersion
            },
            crs: staticQuery.data.crs
        } as AddressesResponse;

        // Update previous ref with current result for future preservation
        previousCombinedRef.current = result;

        return result;
    }, [
        useAggregations,
        staticQuery.data,
        deliveriesQuery.data,
        params.campaignId
    ]);

    // Return appropriate query based on zoom level
    if (useAggregations) {
        return {
            data: aggregationsQuery.data,
            isLoading: aggregationsQuery.isLoading,
            isError: aggregationsQuery.isError,
            error: aggregationsQuery.error,
            isFetching: aggregationsQuery.isFetching,
            isZoomedOut: true
        };
    }

    return {
        data: combinedAddresses,
        isLoading: staticQuery.isLoading || deliveriesQuery.isLoading,
        isError: staticQuery.isError || deliveriesQuery.isError,
        error: staticQuery.error || deliveriesQuery.error,
        isFetching: staticQuery.isFetching || deliveriesQuery.isFetching,
        isZoomedOut: false
    };
};

/**
 * React Query hook for fetching postal code polygons
 */
export const usePostalCodes = (
    params: PostalCodesQueryParams,
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    const queryParams: PostalCodesQueryParams = useMemo(
        () => ({
            ...params,
            bbox
        }),
        [params, bbox]
    );

    return useQuery<PostalCodesResponse, Error>({
        queryKey: ["map", "postal-codes", queryParams],
        queryFn: async () => {
            try {
                const result = await fetchPostalCodes(queryParams);
                return result;
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "postal-codes", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false,
        refetchInterval: options?.refetchInterval,
        staleTime: options?.staleTime ?? 24 * 60 * 60 * 1000, // 24 hours default (postal codes are static)
        placeholderData: (previousData) => previousData,
        gcTime: 24 * 60 * 60 * 1000 // Keep cache for 24 hours
    });
};

/**
 * React Query hook for fetching electoral district polygons
 */
export const useElectoralDistricts = (
    params: ElectoralDistrictsQueryParams,
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    const queryParams: ElectoralDistrictsQueryParams = useMemo(
        () => ({
            ...params,
            bbox
        }),
        [params, bbox]
    );

    return useQuery<ElectoralDistrictsResponse, Error>({
        queryKey: ["map", "electoral-districts", queryParams],
        queryFn: async () => {
            try {
                const result = await fetchElectoralDistricts(queryParams);
                return result;
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "electoral-districts", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false,
        refetchInterval: options?.refetchInterval,
        staleTime: options?.staleTime ?? 24 * 60 * 60 * 1000, // 24 hours default (electoral districts are static)
        placeholderData: (previousData) => previousData,
        gcTime: 24 * 60 * 60 * 1000 // Keep cache for 24 hours
    });
};

/**
 * React Query hook for fetching municipality (kommun) polygons
 */
export const useMunicipalities = (
    params: MunicipalitiesQueryParams,
    map?: MapboxMap | null,
    options?: MapQueryOptions
) => {
    const telemetry = useTelemetry();

    const bbox = useMemo(() => {
        if (params.bbox) {
            return params.bbox;
        }
        if (map) {
            return getBoundingBoxFromMap(map);
        }
        return undefined;
    }, [params.bbox, map]);

    const queryParams: MunicipalitiesQueryParams = useMemo(
        () => ({
            ...params,
            bbox
        }),
        [params, bbox]
    );

    return useQuery<MunicipalitiesResponse, Error>({
        queryKey: ["map", "municipalities", queryParams],
        queryFn: async () => {
            try {
                const result = await fetchMunicipalities(queryParams);
                return result;
            } catch (error) {
                telemetry.trackError("map.fetch.failure", { source: "municipalities", error, bbox });
                throw error;
            }
        },
        enabled: options?.enabled !== false,
        refetchInterval: options?.refetchInterval,
        staleTime: options?.staleTime ?? 24 * 60 * 60 * 1000, // 24 hours default (municipalities are static)
        placeholderData: (previousData) => previousData,
        gcTime: 24 * 60 * 60 * 1000 // Keep cache for 24 hours
    });
};

/**
 * React Query hook for fetching counties (län) for the map layers dropdown
 */
export const useCounties = (options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: ["map", "counties"],
        queryFn: () => fetchCounties(),
        enabled: options?.enabled !== false,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000
    });
};